import React, { useState, useEffect } from 'react';
import GuestbookMap from '../components/GuestbookMap';
import GuestbookForm from '../components/GuestbookForm';
import { ScrollArea } from '../components/ui/scroll-area';
import { guestbookAPI, settingsAPI } from '../services/api';
import { toast } from 'sonner';

const HomePage = () => {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  // Fetch entries and settings on mount
  useEffect(() => {
    fetchEntries();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await settingsAPI.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if fetch fails
      setSettings({
        hero_title: "¡Celebra con nosotros!",
        hero_text: "Únete a nuestra celebración dejando tu mensaje en el libro de visitas interactivo. Cada mensaje aparecerá en el mapa mundial, conectando recuerdos de todo el planeta.",
        cta_button_text: "✍️ Dejar un mensaje",
        cta_button_text_alt: "🗺️ Ver el mapa",
        map_title: "🌍 Mensajes desde el mundo",
        map_description: "Haz clic en los marcadores para leer los mensajes de nuestra comunidad",
        messages_title: "💬 Últimos mensajes",
        footer_text: "© 2026 Enfusión - 20 años de pasión digital",
        stat_messages_label: "Mensajes",
        stat_locations_label: "Ubicaciones",
        stat_years_label: "Años",
        stat_years_number: 20
      });
    }
  };

  const fetchEntries = async () => {
    try {
      setIsLoading(true);
      const data = await guestbookAPI.getEntries();
      setEntries(data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Error al cargar los mensajes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    // After successful submission, switch back to map view and reload entries
    setShowForm(false);
    // Reload entries to show the new message on the map
    await fetchEntries();
  };

  if (!settings) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#000',
        color: '#56cea4',
        fontSize: '1.5rem'
      }}>
        Cargando...
      </div>
    );
  }

  return (
    <div className="homepage">
      {/* Header */}
      <header className="retro-header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_interactive-memories/artifacts/ucjrncw8_logo-isotipo-enfusion-2025-3d.png" 
              alt="Enfusión Logo" 
              className="logo"
            />
          </div>
          <div className="header-text">
            <h1 className="main-title">
              <span className="title-line">20 Años de</span>
              <span className="title-brand">ENFUSIÓN</span>
            </h1>
            <p className="subtitle">2006 - 2026 | Pensado por estrategas, desarrollado por románticos.</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-avatars">
              <img 
                src="https://customer-assets.emergentagent.com/job_interactive-memories/artifacts/2fskdbvt_avatares-nico-rique-ok.png" 
                alt="Nico y Henrique" 
                className="avatars-image"
              />
            </div>
            <h2 className="hero-title">{settings.hero_title}</h2>
            <p className="hero-text">{settings.hero_text}</p>
            <button 
              onClick={() => setShowForm(!showForm)} 
              className="cta-button"
            >
              {showForm ? settings.cta_button_text_alt : settings.cta_button_text}
            </button>
          </div>
        </section>

        {/* Map or Form Section */}
        <section className="interactive-section">
          {!showForm ? (
            <div className="map-section">
              <h2 className="section-title">{settings.map_title}</h2>
              <p className="section-description">{settings.map_description}</p>
              
              {isLoading ? (
                <div style={{ 
                  height: '600px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#56cea4',
                  fontSize: '1.5rem'
                }}>
                  Cargando mensajes...
                </div>
              ) : (
                <GuestbookMap entries={entries} />
              )}
              
              {/* Stats */}
              <div className="stats-container">
                <div className="stat-card">
                  <span className="stat-number">{entries.length}</span>
                  <span className="stat-label">{settings.stat_messages_label}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">
                    {new Set(entries.map(e => e.location.split(',').pop()?.trim())).size}
                  </span>
                  <span className="stat-label">{settings.stat_locations_label}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{settings.stat_years_number}</span>
                  <span className="stat-label">{settings.stat_years_label}</span>
                </div>
              </div>
            </div>
          ) : (
            <GuestbookForm onSubmit={handleSubmit} />
          )}
        </section>

        {/* Message List */}
        {!showForm && entries.length > 0 && (
          <section className="messages-section">
            <h2 className="section-title">{settings.messages_title}</h2>
            <ScrollArea className="messages-scroll" style={{ maxHeight: '800px' }}>
              <div className="messages-grid">
                {[...entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((entry) => (
                  <div key={entry.id} className="message-card">
                    <div className="message-header">
                      <span className="message-author">{entry.name}</span>
                      <span className="message-location">📍 {entry.location}</span>
                    </div>
                    <p className="message-text">{entry.message}</p>
                    {entry.image && (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${entry.image}`} 
                        alt="Memoria" 
                        className="message-image" 
                      />
                    )}
                    <span className="message-date">
                      {new Date(entry.timestamp).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="retro-footer">
        <div className="footer-content">
          <p>{settings.footer_text}</p>
          <div className="footer-links">
            <span className="visitor-counter">👁️ {settings.stat_messages_label}: {entries.length}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
